import { DialogComponent } from '@theme/dialog';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { isVariantPickerFullySelected } from '@theme/utilities';

/** @typedef {import('./types.js').ProductVariant} ProductVariant */

class SizeDrawer extends DialogComponent {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @returns {HTMLElement | undefined} */
  get #valueLabel() {
    const el = this.refs.valueLabel;
    return el instanceof HTMLElement ? el : undefined;
  }

  /** @returns {Element | undefined} */
  get #picker() {
    const el = this.refs.picker;
    return el instanceof Element ? el : undefined;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }
    const { signal } = this.#abortController;

    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // Delegated from the host, not bound on the picker: a color swap morphs the whole section and
    // may replace the picker node (cf. product-color-swap.js). Bonnet radios never reach us — the
    // fake filter stops their `change` events (bonnet-filter.js).
    this.addEventListener('change', this.#onDrawerPickerChange, { signal });

    // Progressive enhancement: if the page already resolved an explicit variant from the URL
    // (e.g. back navigation, shared link), reflect it in the label without requiring a click.
    if (this.#picker && window.location.search.includes('variant=')) {
      this.#updateLabelFromPicker(this.#picker);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * The drawer's own picker changed: forward the full selection to the main picker so the
   * product form + price stay authoritative, then close the drawer once every option is set.
   * @param {Event} event
   */
  #onDrawerPickerChange = (event) => {
    const drawerPicker = this.#picker;
    const mainPicker = this.#mainPicker;
    if (!drawerPicker || !mainPicker) return;

    const { target } = event;
    if (!(target instanceof Node) || !drawerPicker.contains(target)) return;

    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedRadios = drawerPicker.querySelectorAll('input[type="radio"][data-option-index]:checked');
    /** @type {HTMLInputElement | null} */
    let lastSynced = null;

    for (const radio of checkedRadios) {
      const index = radio.dataset.optionIndex;
      /** @type {HTMLInputElement | null} */
      const targetRadio = mainPicker.querySelector(
        `input[type="radio"][data-option-index="${index}"][value="${CSS.escape(radio.value)}"]`
      );
      if (!targetRadio) continue;

      targetRadio.checked = true;
      lastSynced = targetRadio;
    }

    // Announced once the whole selection is mirrored, and announced even when every radio already
    // matched: the main picker starts on the variant Shopify resolved server-side, so choosing that
    // very size leaves it untouched. Staying silent there emitted no `variant:update`, so the
    // add-to-cart intent waiting for one (`redirectAddToCartToSizeDrawer` in utilities.js) was
    // dropped as the drawer closed: the layer selected the size and added nothing (recette
    // 2026-09-02, most visible right after a color swap since every size choice is then a first one).
    lastSynced?.dispatchEvent(new Event('change', { bubbles: true }));

    this.#updateLabelFromPicker(drawerPicker);

    if (isVariantPickerFullySelected(drawerPicker)) this.closeDrawer();
  };

  /**
   * Keep the drawer's picker in sync when the variant changes from elsewhere (sticky bar, etc.).
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;
    if (
      event.detail?.data?.productId &&
      this.dataset.productId &&
      event.detail.data.productId !== this.dataset.productId
    ) {
      return;
    }

    const source = event.composedPath()[0];
    if (source instanceof Node && this.contains(source)) return; // avoid feedback loop

    const variant = /** @type {ProductVariant | null} */ (event.detail?.resource);
    const drawerPicker = this.#picker;
    if (!variant || !drawerPicker) return;

    variant.options?.forEach((value, index) => {
      /** @type {HTMLInputElement | null} */
      const radio = drawerPicker.querySelector(
        `input[type="radio"][data-option-index="${index}"][value="${CSS.escape(value)}"]`
      );
      if (radio && !radio.checked) radio.checked = true;
    });

    this.#updateLabelFromPicker(drawerPicker);
  };

  /** @returns {Element | null} */
  get #mainPicker() {
    const section = this.closest('.shopify-section');
    return section?.querySelector('variant-picker:not(size-drawer variant-picker)') ?? null;
  }

  /** @param {Element} picker */
  #updateLabelFromPicker(picker) {
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedRadios = picker.querySelectorAll('input[type="radio"][data-option-index]:checked');
    if (checkedRadios.length === 0) return;

    const values = Array.from(checkedRadios)
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
      .map((radio) => radio.value);

    const valueLabel = this.#valueLabel;
    if (valueLabel) valueLabel.textContent = values.join(' / ');
  }

  openDrawer() {
    this.showDialog();
  }

  closeDrawer() {
    this.closeDialog();
  }
}

if (!customElements.get('size-drawer')) {
  customElements.define('size-drawer', SizeDrawer);
}
